import {View} from '@fantasmagames/slot/src/common/view/View';
import {SpriteButton} from '@fantasmagames/core/src/display/SpriteButton';
import {SymbolSizes} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameConfig, RecordData} from '@fantasmagames/slot/src/config/GameConfig';

/**
 * cheat name (button caption): actual jmx text
 */
export interface Cheats {
	[cheatName: string]: string;
}

export class CheatView extends View {
	protected _background: PIXI.Sprite;
	protected _currentCheat: PIXI.Text;
	protected _infoText: PIXI.Text;
	protected _configByCheatName: RecordData;
	protected _configByCheatPath: RecordData;

	protected _cheats: Cheats = {};

	public init(): void {
		super.init();

		this.container.anchor.set(0.5, 0);

		this.initConfig();

		this.initializeCheatsList();

		this.initializeBackground();

		this.initializeSpinCheatButtons();

		this.initializeInfoText();

		this.initializeDateStamp();

		this.correctContainerPositionFromDevice();

		this.hide();
	}

	protected initConfig(): void {
		const gameConfig = new GameConfig();
		gameConfig.loadConfig(this.resources.getConfig('config.json'));

		if (gameConfig && gameConfig.getConfigByName('cheatsNames')) {
			this._configByCheatName = gameConfig.getConfigByName('cheatsNames');
		}

		if (gameConfig && gameConfig.getConfigByName('cheatsPaths')) {
			this._configByCheatPath = gameConfig.getConfigByName('cheatsPaths');
		}
	}

	protected correctContainerPositionFromDevice(): void {
	}

	public addMathVersion(mathVersion: string): void {
		this._infoText.text += ` Math: ${mathVersion}`;
	}

	protected initializeDateStamp(): void {
		const style = new PIXI.TextStyle({
			fontFamily: 'Arial',
			fontSize: 18,
			fontWeight: 'bold',
			fill: ['#ffffff'],
		});

		this._infoText = new PIXI.Text(`Date: ${__BUILD_DATE__} Branch:${__BRANCH__} Version: ${__VERSION__}`, style);
		this._infoText.position.set(50, 850);

		this.container.addChild(this._infoText);
	}

	protected initializeSpinCheatButtons(): void {
		const style = new PIXI.TextStyle({
			fontFamily: 'Arial',
			fontSize: 18,
			fontWeight: 'bold',
			fill: ['#ffffff'],
		});

		this._currentCheat = new PIXI.Text('SpinCheats: ', style);
		this._currentCheat.position.set(65, 25);
		this.container.addChild(this._currentCheat);

		const elementsPerColumn: number = this.device.isMobile ? 30 : 25;

		let it: number = 0;
		const buttonSize = this.getButtonDimensions();
		const xOffset: number = 25;
		const extraYOffset: number = 5;

		for (const cheat in this._cheats) {
			if (cheat) {
				const btn: SpriteButton = this.createButton(cheat);
				const yOffset = it / elementsPerColumn;
				btn.position.set(
					50 + Math.trunc(yOffset) * (buttonSize.width + xOffset),
					50 + (buttonSize.height + extraYOffset) * (it % elementsPerColumn),
				);
				btn.callback = () => {
					this.setCheat(cheat, this._cheats[cheat]);
				};
				this.container.addChild(btn);

				it++;
			}
		}
	}

	protected setCheat(cheatName: string, cheatText: string): void {
		if (!this._configByCheatPath || (!this._configByCheatPath.getParameter(cheatText)
			&& cheatText !== null)) {
			console.error(`CheatView::path for ${cheatText} cheat should be be added to config.json`);

			return;
		}

		this._currentCheat.text = `Current cheat: ${cheatName}`;
		(window as any).cheat = cheatText;
		(window as any).cheatPath = this._configByCheatPath.getParameter(cheatText);
	}

	protected initializeInfoText(): void {
		const style = new PIXI.TextStyle({
			fontFamily: 'Arial',
			fontSize: 24,
			fontWeight: 'bold',
			fill: ['#ffffff'],
		});

		this._currentCheat = new PIXI.Text('Current cheat: ', style);
		this._currentCheat.position.set(25, -110);
		this.container.addChild(this._currentCheat);
	}

	protected initializeBackground(): void {
		const backgroundGraphics: PIXI.Graphics = new PIXI.Graphics();

		backgroundGraphics.lineStyle(5, 0x000000, 1);
		backgroundGraphics.beginFill(0x000000, 0.25);
		if (this.device.isMobile) {
			backgroundGraphics.drawRoundedRect(0, 0, 720, 1280, 15);
		} else {
			backgroundGraphics.drawRoundedRect(0, 0, 1280, 870, 15);
		}
		backgroundGraphics.endFill();

		this.container.addChild(backgroundGraphics);
	}

	protected initializeCheatsList(): void {
	}

	protected getButtonDimensions(): SymbolSizes {
		return this.device.isMobile ? {width: 200, height: 35} : {width: 150, height: 25};
	}

	protected createButton(caption: string): SpriteButton {
		const button: SpriteButton = new SpriteButton();

		const basicStyle = new PIXI.TextStyle({
			fontFamily: 'Arial',
			fontSize: this.device.isMobile ? 24 : 18,
			fontStyle: 'italic',
			fontWeight: 'bold',
			fill: ['#ffffff', '#00ff99'], // gradient
		});

		const rectSize = this.getButtonDimensions();

		const basicSprite: PIXI.Sprite = new PIXI.Sprite();
		const basicGraphics: PIXI.Graphics = new PIXI.Graphics();
		const basicText: PIXI.Text = new PIXI.Text(caption, basicStyle);
		basicText.anchor.set(0.5);
		basicText.position.set(rectSize.width / 2, rectSize.height / 2);

		basicGraphics.lineStyle(2, 0xff00ff, 1);
		basicGraphics.beginFill(0xff00bb, 0.25);
		basicGraphics.drawRoundedRect(0, 0, rectSize.width, rectSize.height, 15);
		basicGraphics.endFill();

		basicSprite.addChild(basicGraphics);
		basicSprite.addChild(basicText);

		const hoverSprite: PIXI.Sprite = new PIXI.Sprite();
		const hoverGraphics: PIXI.Graphics = new PIXI.Graphics();
		const hoverText: PIXI.Text = new PIXI.Text(caption, basicStyle);
		hoverText.anchor.set(0.5);
		hoverText.position.set(rectSize.width / 2, rectSize.height / 2);

		hoverGraphics.lineStyle(2, 0xff88ff, 1);
		hoverGraphics.beginFill(0xff88bb, 0.25);
		hoverGraphics.drawRoundedRect(0, 0, rectSize.width, rectSize.height, 5);
		hoverGraphics.endFill();

		hoverSprite.addChild(hoverGraphics);
		hoverSprite.addChild(hoverText);

		const downSprite: PIXI.Sprite = new PIXI.Sprite();
		const downGraphics: PIXI.Graphics = new PIXI.Graphics();
		const downText: PIXI.Text = new PIXI.Text(caption, basicStyle);
		downText.anchor.set(0.5);
		downText.position.set(rectSize.width / 2, rectSize.height / 2);

		downGraphics.lineStyle(2, 0xffffff, 1);
		downGraphics.beginFill(0xffffbb, 0.25);
		downGraphics.drawRoundedRect(0, 0, rectSize.width, rectSize.height, 5);
		downGraphics.endFill();

		downSprite.addChild(downGraphics);
		downSprite.addChild(downText);

		button.setFrames(basicSprite, downSprite, hoverSprite);

		return button;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/cheat/CheatView.ts